import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipToContent from "@/components/SkipToContent";
import { Home, ChevronRight, ExternalLink } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";

const Sitemap = () => {
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  const toggleCategory = (category: string) => {
    setOpenCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const sitemapData = [
    {
      category: "Main Pages",
      icon: "🏠",
      links: [
        { title: "Home", url: "/", desc: "Homepage and landing page" },
        { title: "About Us", url: "/about", desc: "Our history, mission, and values" },
        { title: "Contact Us", url: "/contact", desc: "Get in touch with us" }
      ]
    },
    {
      category: "Products & Services",
      icon: "💼",
      links: [
        { title: "All Products", url: "/products", desc: "View all SACCO products and services" },
        { title: "Member Deposits", url: "/products#savings", desc: "Savings account options" },
        { title: "Normal (Development) Loan", url: "/products#loans", desc: "Standard loan product" },
        { title: "Emergency Loan", url: "/products#loans", desc: "Quick emergency financing" },
        { title: "Education Loan", url: "/products#loans", desc: "Loans for educational expenses" },
        { title: "Device Loan", url: "/products#loans", desc: "Loans for purchasing devices" }
      ]
    },
    {
      category: "Membership",
      icon: "👥",
      links: [
        { title: "Membership Information", url: "/membership", desc: "Learn about becoming a member" },
        { title: "Membership Application", url: "/membership-application", desc: "Apply for membership online" }
      ]
    },
    {
      category: "Member Portal",
      icon: "🔐",
      links: [
        { title: "Member Self Service", url: "https://selfservice.natconsacco.or.ke/", desc: "Access your account online", external: true },
        { title: "Sign In", url: "/auth", desc: "Login to your account" }
      ]
    },
    {
      category: "Resources & Tools",
      icon: "🧰",
      links: [
        { title: "Loan Calculator", url: "/calculator", desc: "Calculate your loan repayments" },
        { title: "FAQs", url: "/faq", desc: "Frequently asked questions" },
        { title: "News & Media", url: "/news-media", desc: "Latest news and updates" }
      ]
    },
    {
      category: "Opportunities",
      icon: "💼",
      links: [
        { title: "Careers", url: "/careers", desc: "Job opportunities at Natcon SACCO" },
        { title: "Tenders", url: "/tenders", desc: "Current tender opportunities" }
      ]
    },
    {
      category: "Legal & Policies",
      icon: "⚖️",
      links: [
        { title: "Privacy Policy", url: "/privacy-policy", desc: "How we protect your personal data" },
        { title: "Terms of Service", url: "/terms-of-service", desc: "Terms and conditions for using our services" },
        { title: "Sitemap", url: "/sitemap", desc: "Complete site navigation (this page)" }
      ]
    }
  ];

  const lastUpdated = "January 15, 2025";

  return (
    <>
      <Helmet>
        <title>Sitemap - Natcon Staff Sacco | Complete Site Navigation</title>
        <meta name="description" content="Complete sitemap of Natcon Staff Sacco website. Find all pages, services, and resources easily in one place." />
        <meta name="keywords" content="Natcon SACCO sitemap, site navigation, website map, page directory" />
        <link rel="canonical" href="https://natconsacco.or.ke/sitemap" />
      </Helmet>

      <SkipToContent />
      <Header />

      <main id="main-content" className="min-h-screen bg-background">
        {/* Breadcrumbs */}
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-3">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
              <a href="/" className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                <Home className="h-4 w-4" />
                <span>Home</span>
              </a>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-foreground font-medium">Sitemap</span>
            </nav>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 lg:py-12">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Website Sitemap</h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Complete navigation guide to help you find information quickly on the Natcon Staff Sacco website.
              </p>
              <div className="inline-flex items-center gap-2 bg-muted px-4 py-2 rounded-lg text-sm mt-4">
                <span className="text-muted-foreground">Last Updated:</span>
                <span className="font-medium">{lastUpdated}</span>
              </div>
            </div>

            {/* Sitemap Grid - Desktop */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-6 mb-12">
              {sitemapData.map((section) => (
                <div key={section.category} className="bg-card border rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{section.icon}</span>
                    <h2 className="text-xl font-bold">{section.category}</h2>
                  </div>
                  <ul className="space-y-3">
                    {section.links.map((link) => (
                      <li key={link.url}>
                        <a 
                          href={link.url}
                          target={link.external ? "_blank" : undefined}
                          rel={link.external ? "noopener noreferrer" : undefined}
                          className="group flex items-start gap-2 text-muted-foreground hover:text-primary transition-colors"
                        >
                          <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                          <div className="flex-1">
                            <div className="font-medium text-foreground group-hover:text-primary flex items-center gap-1">
                              {link.title}
                              {link.external && <ExternalLink className="h-3 w-3" />}
                            </div>
                            <div className="text-xs mt-0.5">{link.desc}</div>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Sitemap Accordion - Mobile */}
            <div className="lg:hidden space-y-4">
              {sitemapData.map((section) => (
                <Collapsible 
                  key={section.category}
                  open={openCategories.includes(section.category)}
                  onOpenChange={() => toggleCategory(section.category)}
                >
                  <div className="bg-card border rounded-lg overflow-hidden">
                    <CollapsibleTrigger className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{section.icon}</span>
                        <h2 className="text-lg font-bold">{section.category}</h2>
                      </div>
                      <ChevronRight className={`h-5 w-5 transition-transform ${openCategories.includes(section.category) ? 'rotate-90' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <ul className="p-4 pt-0 space-y-3 border-t">
                        {section.links.map((link) => (
                          <li key={link.url}>
                            <a 
                              href={link.url}
                              target={link.external ? "_blank" : undefined}
                              rel={link.external ? "noopener noreferrer" : undefined}
                              className="group flex items-start gap-2 text-muted-foreground hover:text-primary transition-colors min-h-[44px] py-2"
                            >
                              <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                              <div className="flex-1">
                                <div className="font-medium text-foreground group-hover:text-primary flex items-center gap-1">
                                  {link.title}
                                  {link.external && <ExternalLink className="h-3 w-3" />}
                                </div>
                                <div className="text-xs mt-0.5">{link.desc}</div>
                              </div>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>

            {/* Additional Info */}
            <div className="mt-12 p-6 bg-primary/5 border border-primary/20 rounded-lg">
              <h2 className="text-xl font-bold mb-4">Need Help Finding Something?</h2>
              <p className="text-muted-foreground mb-4">
                If you can't find what you're looking for, please don't hesitate to contact us. Our team is here to help you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="/contact" 
                  className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors min-h-[44px]"
                >
                  Contact Us
                  <ChevronRight className="h-4 w-4" />
                </a>
                <a 
                  href="/faq" 
                  className="inline-flex items-center justify-center gap-2 bg-secondary text-secondary-foreground px-6 py-3 rounded-lg font-medium hover:bg-secondary/80 transition-colors min-h-[44px]"
                >
                  View FAQs
                  <ChevronRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* XML Sitemap Link */}
            <div className="mt-8 text-center text-sm text-muted-foreground">
              <p>
                For search engines: <a href="/sitemap.xml" className="text-primary hover:underline">XML Sitemap</a>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default Sitemap;
